// Advertiser analytics domain — the dashboard's performance + spend response.
// Shared by the backend (GET /analytics) and the advertiser portal. All money
// is in cents (`*Cents`); see AdvertiserBalance / Campaign.budget for the
// dollars-vs-cents conventions elsewhere. Schemas are authoritative; the TS
// types are derived via z.infer<>.

import { z } from 'zod';

export const DailyStatSchema = z.object({
  date: z.string(),
  impressions: z.number(),
  clicks: z.number(),
  ctr: z.number(),
  spendCents: z.number(),
});
export type DailyStat = z.infer<typeof DailyStatSchema>;

export const AdStatSchema = z.object({
  adId: z.string(),
  adTitle: z.string(),
  impressions: z.number(),
  clicks: z.number(),
  ctr: z.number(),
});
export type AdStat = z.infer<typeof AdStatSchema>;

export const CampaignSpendSchema = z.object({
  campaignId: z.string(),
  campaignName: z.string(),
  spendCents: z.number(),
  impressions: z.number(),
  clicks: z.number(),
});
export type CampaignSpend = z.infer<typeof CampaignSpendSchema>;

export const AnalyticsSpendSchema = z.object({
  totalSpendCents: z.number(),
  // No avgCpcCents: the platform bills CPM-only. Clicks are free, so a cost-per-click
  // figure is always $0 and would misrepresent the billing model. Click COUNTS (not cost)
  // still surface via totalClicks / daily / byAd / byCampaign.
  cpmCents: z.number(),
  byCampaign: z.array(CampaignSpendSchema),
});
export type AnalyticsSpend = z.infer<typeof AnalyticsSpendSchema>;

/** The advertiser dashboard analytics response (GET /analytics?days=N). */
export const AdvertiserAnalyticsSchema = z.object({
  totalImpressions: z.number(),
  totalClicks: z.number(),
  ctr: z.number(),
  daily: z.array(DailyStatSchema),
  byAd: z.array(AdStatSchema),
  spend: AnalyticsSpendSchema,
  balanceCents: z.number(),
});
export type AdvertiserAnalytics = z.infer<typeof AdvertiserAnalyticsSchema>;
